import {
  useConnectedBankAccount,
  useStripeActions,
  type BankAccount,
} from "../../bank-accounts";
import type {BankAccountStatus} from "../../bank-accounts/domain/bank-account.types";
import type {PaymentProvider} from "../domain/currency.types";
import {filterCurrenciesByProvider} from "../domain/currency.service";
import {useCurrencies} from "./useCurrencies";

const BANK_NOT_CONNECTED_MESSAGE =
  "Setup your payout details to enable you receive your earnings.";
const STRIPE_INACTIVE_MESSAGE =
  "Complete your Stripe registration to start creating paid offerings and receive payment securely";

export interface PricingCurrenciesResult {
  formCurrencies: string[];
  paymentProvider: PaymentProvider;
  connectedBankStatus: {
    isBankConnected: boolean;
    status: BankAccountStatus | null;
    /** Human-readable message when bank is not connected (for tooltips). */
    errorMessageString: string;
  };
  isLoading: boolean;
  isUsingFallbackCurrencies: boolean;
  bankAccount: BankAccount | null;
  handleCompleteStripeRegistration: () => void;
  handleLoginToStripe: () => void;
}

/**
 * Composes useCurrencies + useConnectedBankAccount.
 * Applies filterCurrenciesByProvider for Stripe (EU-only).
 * Returns shape compatible with previous currency context consumers.
 */
export function usePricingCurrencies(): PricingCurrenciesResult {
  const currenciesResult = useCurrencies();
  const bankQuery = useConnectedBankAccount("currency_provider");
  const {completeRegistration, loginToStripe} = useStripeActions();

  const bankAccount = bankQuery.data ?? null;
  const provider: PaymentProvider | null = bankAccount
    ? (bankAccount.provider as PaymentProvider)
    : null;

  const formCurrencies = filterCurrenciesByProvider({
    currencies: currenciesResult.currencies,
    provider,
  });

  const errorMessageString = !bankAccount
    ? BANK_NOT_CONNECTED_MESSAGE
    : bankAccount.isStripeAndInactive
    ? STRIPE_INACTIVE_MESSAGE
    : "";

  return {
    formCurrencies,
    paymentProvider: (provider ?? "flutterwave") as PaymentProvider,
    connectedBankStatus: {
      isBankConnected: !!bankAccount,
      status: bankAccount?.status ?? null,
      errorMessageString,
    },
    isLoading: currenciesResult.isLoading || bankQuery.isLoading,
    isUsingFallbackCurrencies: currenciesResult.isUsingFallback,
    bankAccount,
    handleCompleteStripeRegistration: completeRegistration,
    handleLoginToStripe: loginToStripe,
  };
}
